import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { AuthContext } from './AuthContext';
import { readOAuthError } from './oauthError';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  // Lazy init: RequireAdmin'in oturumsuz kullanıcıyı /login'e yönlendirmesinden
  // önce, ilk render'da URL'deki olası OAuth hatasını bir kere okur.
  const [oauthError, setOauthError] = useState<string | null>(() => readOAuthError());

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });

    const { data: subscription } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
    });

    return () => subscription.subscription.unsubscribe();
  }, []);

  const value = useMemo(
    () => ({
      session,
      loading,
      oauthError,
      async signIn(email: string, password: string) {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        return { error: error?.message ?? null };
      },
      async signInWithGoogle() {
        setOauthError(null);
        const { error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: { redirectTo: window.location.origin },
        });
        // Başarılıysa tarayıcı Google'a yönlenir; buradaki hata yalnız
        // yönlendirme HİÇ gerçekleşmeden (ör. client hatası) döner. Sağlayıcı
        // Supabase'de kapalıysa hata ancak redirect dönüşünde URL'e eklenir
        // (bkz. oauthError.ts / readOAuthError).
        return { error: error?.message ?? null };
      },
      async signOut() {
        await supabase.auth.signOut();
      },
    }),
    [session, loading, oauthError],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
